'use client';

import { useState } from 'react';
import { useResumeBuilderStore } from '@/stores/resume-builder-store';
import { Input } from '@/components/ui/forms/input';
import { Label } from '@/components/ui/forms/label';
import { Button } from '@/components/ui/forms/button';
import { Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { generateId } from '../../utils';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/layout/collapsible';

export default function EducationSection() {
  const { resume, addEducation, updateEducation, removeEducation } = useResumeBuilderStore();
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  if (!resume) return null;

  const { education } = resume.content;

  const handleAdd = () => {
    const id = generateId();
    addEducation({
      id,
      institution: '',
      degree: '',
      field: '',
      startDate: '',
      endDate: '',
      gpa: '',
    });
    setExpandedIds((prev) => new Set([...prev, id]));
  };

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Sort by end date descending
  const sortedEducation = [...education].sort((a, b) => {
    if (!a.endDate) return -1;
    if (!b.endDate) return 1;
    return b.endDate.localeCompare(a.endDate);
  });

  return (
    <div className="space-y-4">
      {sortedEducation.length === 0 ? (
        <p className="text-body text-muted-foreground">
          Add your educational background, including degrees, certifications, and relevant coursework.
        </p>
      ) : (
        <div className="space-y-3">
          {sortedEducation.map((edu) => (
            <Collapsible
              key={edu.id}
              open={expandedIds.has(edu.id)}
              onOpenChange={() => toggleExpanded(edu.id)}
            >
              <div className="rounded-lg border border-border bg-card">
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between p-4 text-left"
                  >
                    <div className="flex-1">
                      <div className="text-label">
                        {edu.degree || 'New Education'}
                        {edu.field && <span className="text-body"> in {edu.field}</span>}
                      </div>
                      {edu.institution && (
                        <div className="text-body-sm text-muted-foreground">{edu.institution}</div>
                      )}
                    </div>
                    {expandedIds.has(edu.id) ? (
                      <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="space-y-4 border-t border-border p-4">
                    <div className="space-y-2">
                      <Label>Institution *</Label>
                      <Input
                        value={edu.institution}
                        onChange={(e) => updateEducation(edu.id, { institution: e.target.value })}
                        placeholder="Stanford University"
                      />
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Degree *</Label>
                        <Input
                          value={edu.degree}
                          onChange={(e) => updateEducation(edu.id, { degree: e.target.value })}
                          placeholder="Bachelor of Science"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Field of Study</Label>
                        <Input
                          value={edu.field || ''}
                          onChange={(e) => updateEducation(edu.id, { field: e.target.value })}
                          placeholder="Computer Science"
                        />
                      </div>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Start Date</Label>
                        <Input
                          type="month"
                          value={edu.startDate || ''}
                          onChange={(e) => updateEducation(edu.id, { startDate: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>End Date</Label>
                        <Input
                          type="month"
                          value={edu.endDate || ''}
                          onChange={(e) => updateEducation(edu.id, { endDate: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>GPA</Label>
                      <Input
                        value={edu.gpa || ''}
                        onChange={(e) => updateEducation(edu.id, { gpa: e.target.value })}
                        placeholder="3.8/4.0"
                      />
                      <p className="text-caption text-muted-foreground">
                        Optional. Include if it's above 3.5 or particularly relevant.
                      </p>
                    </div>

                    <div className="flex justify-end">
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => removeEducation(edu.id)}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Remove
                      </Button>
                    </div>
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          ))}
        </div>
      )}

      <Button type="button" variant="outline" onClick={handleAdd}>
        <Plus className="mr-2 h-4 w-4" />
        Add Education
      </Button>
    </div>
  );
}
